import { Container } from "pixi.js";
import { BILLBOARD_ASSET_ID, fillBillboard } from "../ui/Billboard";
import type { CityAdvertisement, CityAsset, CityObjectData } from "../api/types";
import { IsoProjection } from "../core/projection";
import {
  assetSpriteFrameCount,
  createAssetSprite,
  setAssetSpriteFrame,
  setAssetSpriteLightLevel,
  setAssetSpriteRotation,
  setAssetSpriteYaw,
  type AssetSpriteView,
} from "../houses/AssetSprite";
import { sampleMotion } from "../motion";

export class ObjectLayer extends Container {
  private readonly assetMap: Map<string, CityAsset>;
  private readonly objectViews = new Map<string, AssetSpriteView>();
  private readonly itemMap = new Map<string, CityObjectData>();
  private selectionHandler: ((item: CityObjectData) => void) | null = null;
  private selectedId: string | null = null;
  private motionPreviewEnabled = false;
  private lightLevel = 0;

  constructor(
    private readonly projection: IsoProjection,
    assets: CityAsset[],
    private advertisements: CityAdvertisement[] = [],
  ) {
    super();
    this.label = "city-objects";
    this.sortableChildren = true;
    this.assetMap = new Map(assets.map((asset) => [asset.assetId, asset]));
  }

  async load(items: CityObjectData[]): Promise<void> {
    for (const child of this.removeChildren()) child.destroy({ children: true });
    this.objectViews.clear();
    this.itemMap.clear();
    items.forEach((item) => this.itemMap.set(item.id, item));

    // Unbounded image decoding can exhaust mobile GPU memory before the first
    // city frame. Limit simultaneous texture uploads without dropping assets.
    const visible = items.filter((item) => item.visible !== false);
    let index = 0;
    const workers = Math.min(
      visible.length,
      window.innerWidth <= 768 ? 4 : 12,
    );
    await Promise.all(Array.from({ length: workers }, async () => {
      while (index < visible.length) {
        await this.createView(visible[index++]);
      }
    }));
    this.applySelection();
    this.sortChildren();
  }

  async refreshAsset(
    asset: CityAsset,
    items: CityObjectData[],
  ): Promise<void> {
    this.assetMap.set(asset.assetId, asset);
    const targets = items.filter(
      (item) => item.visible !== false && item.assetId === asset.assetId,
    );
    for (const item of targets) {
      this.itemMap.set(item.id, item);
      const current = this.objectViews.get(item.id);
      if (current) {
        this.objectViews.delete(item.id);
        if (current.parent === this) this.removeChild(current);
        current.destroy({ children: true });
      }
    }
    await Promise.all(targets.map((item) => this.createView(item)));
    this.applySelection();
    this.sortChildren();
  }

  private async createView(item: CityObjectData): Promise<void> {
    const asset = this.assetMap.get(item.assetId);
    const url = asset?.sourceUrl;
    if (!asset || !url) return;

    try {
      const view = await createAssetSprite(
        {
          assetId: item.assetId,
          x: item.x,
          y: item.y,
          elevation: item.elevation,
          scale: item.scale,
          scaleX: item.scaleX,
          scaleY: item.scaleY,
          rotation: item.rotation,
          yaw: item.yaw ?? 0,
          layerPriority: item.layerPriority,
        },
        asset,
        url,
        this.projection,
      );
      if (item.assetId === BILLBOARD_ASSET_ID) {
        await fillBillboard(view, this.advertisements.find(ad => ad.objectId === item.id));
      }
      view.label = `city-object:${item.id}`;
      setAssetSpriteLightLevel(view, this.lightLevel);
      view.eventMode = "static";
      view.cursor = item.locked ? "pointer" : "move";
      view.on("pointertap", () => this.selectionHandler?.(item));
      this.objectViews.set(item.id, view);
      this.addChild(view);
      this.updateObjectTransform(item);
    } catch {
      // One broken optional decoration must never prevent the city loading.
    }
  }

  async setBillboardCampaign(item: CityObjectData, campaign: CityAdvertisement | null): Promise<void> {
    this.advertisements = this.advertisements.filter(ad => ad.objectId !== item.id);
    if (campaign) this.advertisements.push({...campaign, objectId:item.id});
    const asset = this.assetMap.get(item.assetId);
    if (asset) await this.refreshAsset(asset, [item]);
  }

  onSelect(handler: (item: CityObjectData) => void): void {
    this.selectionHandler = handler;
  }

  addAssets(assets: CityAsset[]): void {
    for (const asset of assets) this.assetMap.set(asset.assetId, asset);
  }

  removeAsset(assetId: string): void {
    this.assetMap.delete(assetId);
    for (const [objectId, item] of this.itemMap) {
      if (item.assetId !== assetId) continue;
      const view = this.objectViews.get(objectId);
      if (view) {
        this.objectViews.delete(objectId);
        if (view.parent === this) this.removeChild(view);
        view.destroy({ children: true });
      }
    }
    if (
      this.selectedId &&
      this.itemMap.get(this.selectedId)?.assetId === assetId
    ) {
      this.selectedId = null;
    }
  }

  setMotionPreview(enabled: boolean): void {
    this.motionPreviewEnabled = enabled;
  }

  setLightLevel(level: number): void {
    this.lightLevel = Math.max(0, Math.min(1, Number.isFinite(level) ? level : 0));
    this.objectViews.forEach((view) => {
      setAssetSpriteLightLevel(view, this.lightLevel);
    });
  }

  isMotionPreviewEnabled(): boolean {
    return this.motionPreviewEnabled;
  }

  setSelected(id: string | null): void {
    this.selectedId = id;
    this.applySelection();
  }

  getView(id: string): Container | null {
    return this.objectViews.get(id) ?? null;
  }

  updateObjectTransform(item: CityObjectData): void {
    const view = this.objectViews.get(item.id);
    const asset = this.assetMap.get(item.assetId);
    if (!view || !asset) return;
    view.cursor = item.locked ? "pointer" : "move";
    this.applyTransform(view, item, asset, item.x, item.y, item.rotation);
    view.cursor = item.locked ? "pointer" : "move";
    this.sortChildren();
  }

  updateTime(seconds: number, editorMode: boolean): void {
    let needsSort = false;
    this.itemMap.forEach((item) => {
      if (item.visible === false) return;
      const view = this.objectViews.get(item.id);
      const asset = this.assetMap.get(item.assetId);
      if (!view || !asset) return;

      const allowMotion = !editorMode || this.motionPreviewEnabled;
      const sample = allowMotion ? sampleMotion(item, seconds) : null;
      let rotation = item.rotation;
      if (sample?.active && item.motion?.orient && sample.direction) {
        const a = this.projection.worldToScreen(0, 0);
        const b = this.projection.worldToScreen(
          sample.direction.x,
          sample.direction.y,
        );
        rotation =
          180 + (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
      }

      this.applyTransform(
        view,
        item,
        asset,
        sample?.x ?? item.x,
        sample?.y ?? item.y,
        rotation,
      );

      const count = assetSpriteFrameCount(view);
      if (count > 1) {
        const forced = sample?.active ? sample.frame : null;
        const fps = sample?.active
          ? Math.max(0.2, Math.min(60, item.motion?.fps ?? 8))
          : 1000 / Math.max(30, Math.min(5000, asset.frameDelayMs ?? 150));
        setAssetSpriteFrame(view, forced ?? Math.floor(seconds * fps));
      }
      needsSort = true;
    });
    if (needsSort) this.sortChildren();
  }

  private applyTransform(
    view: AssetSpriteView,
    item: CityObjectData,
    asset: CityAsset,
    x: number,
    y: number,
    rotation: number,
  ): void {
    const offset = this.projection.worldToScreen(x, y, item.elevation);
    view.position.set(
      offset.x + (asset.visualOffsetX ?? 0),
      offset.y + (asset.visualOffsetY ?? 0),
    );
    view.zIndex =
      item.layerPriority * 10000 +
      x +
      y +
      (asset.renderOrder ?? 0) * 100;
    setAssetSpriteRotation(view, rotation);
    setAssetSpriteYaw(view, item.yaw ?? 0);
  }

  private applySelection(): void {
    this.objectViews.forEach((view, id) => {
      const selected = id === this.selectedId;
      view.alpha = selected ? 1 : 0.98;
      view.scale.set(selected ? 1.035 : 1);
    });
  }
}
